import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import {
  humanizeKey,
  PROJECT_QUALITY_GUARD_PANEL_FOCUS,
  PROJECT_SEARCH_PARAM_KEY,
  type ProjectQualityGuardPanelFocus,
  resolveEntityAuthoringTabDescriptors,
} from "@repo/shared";
import { useQuery } from "convex/react";
import {
  CircleDashed,
  ScanLine,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  ShieldQuestionMark,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { AdminFormDialog } from "@/components/ui/admin-form-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusPanel } from "@/components/ui/status-panel";
import { Textarea } from "@/components/ui/textarea";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import { resolveUser, useOrgUserMap } from "@/hooks/use-org-user-map";
import { trackQualityGuardEvent } from "@/lib/analytics/domain";
import { getConvexErrorMessage } from "@/lib/convex-errors";
import {
  notifyQualityGuardScanStarted,
  QUALITY_GUARD_ENTITY_SCAN_STARTED_MESSAGE,
} from "@/lib/quality-guard-toasts";
import { cn } from "@/lib/utils";
import { useRightSidebarClaudia, useRightSidebarEntity, useRightSidebarUi } from "../../contexts";
import { useProjectFrameworkStructure } from "../../hooks";
import { useProjectSearchParams } from "../../routing/project-route-context";
import {
  buildClaudiaFixIntent,
  type QualityGuardFixFindingInput,
} from "./build-claudia-fix-intent";
import { QualityGuardFindingCard } from "./finding-card";
import {
  combineQualityGuardIssueState,
  formatQualityGuardEvaluationCounts,
  formatQualityGuardIssueCount,
  normalizeScopeSeverity,
  QUALITY_GUARD_OPEN_WORK_GROUP_KEY,
  QualityGuardAcceptedRiskBadge,
  type QualityGuardEvaluationCounts,
  type QualityGuardEvaluationState,
  QualityGuardIssueBadge,
  QualityGuardOpenWorkTag,
  type QualityGuardScopeSeverity,
  qualityGuardAcceptedRiskVisuals,
} from "./issue-badge";
import {
  acceptRiskRefusalReason,
  deriveHeaderProjectionFromFindings,
  findingMatchesSection,
  getFindingRemediation,
  groupOpenFindings,
  QUALITY_GUARD_ACCEPTED_GROUP_LABEL,
  type QualityGuardAcceptedRisk,
  type QualityGuardFinding,
  type QualityGuardHeaderProjection,
  type QualityGuardRiskTarget,
  type QualityGuardSectionProjection,
  qualityGuardFindingRowKey,
  resolveFindingCardLocation,
  resolveFindingCardTitle,
  riskMatchesSection,
  sortFindings,
} from "./quality-guard-types";

/**
 * The entity-scope reading the summary header shows: the engine of record's, never a
 * blend of both.
 *
 * The backend answers `projection`, `sectionProjections`, `findings` and `acceptedRisks`
 * from whichever engine is of record, so this panel switches by data and not by flag —
 * `qg2Projection` is read only to tell "QG2 is of record and has judged nothing here yet"
 * (shown as unchecked, the honest reading) apart from "the incumbent has nothing".
 */
function entityHeaderProjection(state: {
  engineOfRecord?: string;
  qg2Projection?: unknown;
  projection?: unknown;
}): QualityGuardHeaderProjection | null {
  if (state.engineOfRecord === "quality_guard_2") {
    return (state.qg2Projection as QualityGuardHeaderProjection | null) ?? null;
  }
  return (state.projection as QualityGuardHeaderProjection | null) ?? null;
}

function parseQualityGuardPanelFocus(
  searchParams: Record<string, unknown>
): { focus: ProjectQualityGuardPanelFocus; findingId: string | null } | null {
  const focus = searchParams[PROJECT_SEARCH_PARAM_KEY.qualityGuardPanelFocus];
  if (
    focus !== PROJECT_QUALITY_GUARD_PANEL_FOCUS.critical &&
    focus !== PROJECT_QUALITY_GUARD_PANEL_FOCUS.high &&
    focus !== PROJECT_QUALITY_GUARD_PANEL_FOCUS.recommended &&
    focus !== PROJECT_QUALITY_GUARD_PANEL_FOCUS.accepted
  ) {
    return null;
  }
  const findingId = searchParams[PROJECT_SEARCH_PARAM_KEY.qualityGuardPanelFindingId];
  return {
    focus,
    findingId: typeof findingId === "string" && findingId.length > 0 ? findingId : null,
  };
}

interface QualityGuardEntityArgs {
  projectId: Id<"projects">;
  entityTypeKey: string;
  entityId: string;
}

const acceptedRiskDateFormatter = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
});

function formatAcceptedRiskDate(acceptedAt: number): string {
  return acceptedRiskDateFormatter.format(new Date(acceptedAt));
}

/**
 * The accepter's name, from the id the engine of record recorded.
 *
 * The id arrives as a plain string from QG2's rows and as an `Id<"users">` from the
 * incumbent's; it addresses the same `users` row either way, and `resolveUser` answers
 * with the shared unresolved label when it names nothing.
 */
function resolveAcceptedByLabel(
  userMap: ReturnType<typeof useOrgUserMap>,
  acceptedBy: Id<"users"> | string | null | undefined
): string {
  return resolveUser(userMap, (acceptedBy as Id<"users"> | null | undefined) ?? null).fullName;
}

function AcceptedRiskContext({
  risk,
  userMap,
}: {
  risk: QualityGuardAcceptedRisk;
  userMap: ReturnType<typeof useOrgUserMap>;
}) {
  const acceptanceReason = risk.reason?.trim();
  return (
    <>
      <p
        data-qg-accepted-risk-attribution="true"
        className="mt-0.5 text-xs leading-relaxed text-muted-foreground"
      >
        Accepted by {resolveAcceptedByLabel(userMap, risk.acceptedBy)} ·{" "}
        {formatAcceptedRiskDate(risk.acceptedAt)}
      </p>
      {acceptanceReason ? (
        <p
          data-qg-accepted-risk-reason="true"
          className="truncate text-xs leading-relaxed text-muted-foreground"
          title={acceptanceReason}
        >
          {acceptanceReason}
        </p>
      ) : null}
    </>
  );
}

function ProjectionIssueRow({
  label,
  issueCount,
  severity,
  checking,
  applicable = true,
}: {
  label: "Completeness" | "Quality";
  issueCount: number | undefined | null;
  severity?: QualityGuardScopeSeverity | null;
  checking?: boolean;
  applicable?: boolean;
}) {
  const normalizedSeverity = normalizeScopeSeverity(severity);
  const statusText = checking ? "Checking" : applicable ? null : "Not applicable";

  return (
    <div
      data-testid={`quality-guard-axis-summary-row-${label.toLowerCase()}`}
      className="grid grid-cols-[6.75rem_minmax(0,1fr)] items-center gap-3"
    >
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="inline-flex min-w-0 items-center justify-start gap-1 text-sm font-medium">
        <QualityGuardIssueBadge
          label={label}
          count={issueCount}
          severity={normalizedSeverity}
          checking={checking}
          applicable={applicable}
        />
        {statusText ? <span className="text-xs text-muted-foreground">{statusText}</span> : null}
      </span>
    </div>
  );
}

// LAT-858 P5: copy for the "no open findings, but coverage is incomplete" callout. Distinct from
// the green "Everything checks out" state — a clean scan only means something once every rule got
// a verdict, so this stays warning-toned instead of reusing the success state.
function formatPartialEvaluationCallout(
  evaluationState: QualityGuardEvaluationState | undefined,
  counts: QualityGuardEvaluationCounts | undefined
): { title: string; description: string } {
  const couldntCheck = counts
    ? formatQualityGuardIssueCount(counts.unverified) + formatQualityGuardIssueCount(counts.errored)
    : 0;
  if (evaluationState === "unchecked") {
    return {
      title: couldntCheck > 0 ? "Couldn't check" : "Not checked yet",
      description:
        couldntCheck > 0
          ? `Quality Guard couldn't evaluate ${couldntCheck} rule${couldntCheck === 1 ? "" : "s"} for this scope.`
          : "Quality Guard hasn't evaluated this scope yet.",
    };
  }
  return {
    title:
      couldntCheck > 0
        ? `Partially checked — ${couldntCheck} rule${couldntCheck === 1 ? "" : "s"} couldn't be evaluated`
        : "Partially checked",
    description:
      "Quality Guard has no open findings so far, but couldn't verify every rule for this scope.",
  };
}

/**
 * Copy for a scope whose every section turned out not to apply to this content — never a
 * claim about WHY (empty, conditionally discharged, structurally hidden — the reason is the
 * backend's, not the copy's). Neither state is a gap (`formatPartialEvaluationCallout`'s
 * claim) nor a pass (`formatCleanScanDescription`'s): there was nothing here to form a
 * verdict about, so the muted state says exactly that instead of claiming either.
 */
function formatNotApplicableEvaluationState(isSectionScoped: boolean): {
  title: string;
  description: string;
} {
  return {
    title: "Nothing to check",
    description: isSectionScoped
      ? "This section does not apply to the current content."
      : "Quality Guard has nothing to check for this entity — none of its sections apply to the current content.",
  };
}

/**
 * The "Everything checks out" description. A scope earns the plain claim that every aspect
 * is filled in only when nothing it covers was left uncovered — a section that does not apply
 * is not a defect, but it was also never judged clean, and naming it "filled in" beside the
 * sections that actually were overstates what the scan found.
 */
function formatCleanScanDescription(
  isSectionScoped: boolean,
  notApplicableSectionCount: number
): string {
  const scope = isSectionScoped ? "section" : "entity";
  if (notApplicableSectionCount > 0) {
    return `Every checked aspect passes. ${notApplicableSectionCount} section${
      notApplicableSectionCount === 1 ? "" : "s"
    } in this ${scope} ${notApplicableSectionCount === 1 ? "does" : "do"} not apply and ${
      notApplicableSectionCount === 1 ? "was" : "were"
    } not checked.`;
  }
  return `All required aspects for this ${scope} are filled in and confirmed.`;
}

function ProjectionAcceptedRiskRow({ acceptedRiskCount }: { acceptedRiskCount: number }) {
  if (acceptedRiskCount <= 0) {
    return null;
  }

  return (
    <div
      data-testid="quality-guard-axis-summary-row-accepted"
      className="grid grid-cols-[6.75rem_minmax(0,1fr)] items-center gap-3"
    >
      <span className="text-xs text-muted-foreground">Accepted</span>
      <QualityGuardAcceptedRiskBadge label="Accepted risks" count={acceptedRiskCount} />
    </div>
  );
}

/**
 * LAT-898: the accessible name is shared with the right-sidebar panel toggles (and asserted by
 * `entity-header`, `right-sidebar`, `accessibility`, `state-orchestration` and `quality-guard`
 * E2E specs) — the control moved onto the shield, the name did not.
 */
const QUALITY_GUARD_TOGGLE_LABEL = "Toggle Quality Guardian panel";
const QUALITY_GUARD_PANEL_ID = "panel-guardian";
/** 24-tall, square, 4px side padding, 3px between shield and count — see notes/01-pencil-design-spec.md. */
const QUALITY_GUARD_TOGGLE_CLASS =
  "inline-flex h-6 items-center gap-[3px] px-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** What an entity still owes, read off the engine of record's own findings: how many items, and what each says. */
function entityOpenWork(state: unknown): { count: number; summaries: string[] } {
  const findings = (state as { findings?: unknown } | undefined)?.findings;
  if (!Array.isArray(findings)) {
    return { count: 0, summaries: [] };
  }
  const owed = findings.filter(
    (finding): finding is { message?: string | null; title?: string | null } =>
      Boolean(finding) && (finding as { axis?: unknown }).axis === QUALITY_GUARD_OPEN_WORK_GROUP_KEY
  );
  return {
    count: owed.length,
    summaries: owed
      .map((finding) => finding.message?.trim() || finding.title?.trim())
      .filter((summary): summary is string => Boolean(summary)),
  };
}

export function QualityGuardEntityIssueChips({
  projectId,
  entityTypeKey,
  entityId,
}: QualityGuardEntityArgs) {
  const { open, openMobile, isMobile, activePanel, togglePanel } = useRightSidebarUi();
  const { entityName, setEntityContext } = useRightSidebarEntity();
  const state = useQuery(api.qualityGuard.getEntityState, {
    projectId,
    entityTypeKey,
    entityId,
  });

  // LAT-898: this shield is the single Guardian control (the grey cluster button is gone), so it
  // is status first and toggle second — square, quiet, and stateful while the panel is open.
  const isGuardianOpen = (isMobile ? openMobile : open) && activePanel === "guardian";
  const guardianToggleProps = {
    type: "button",
    "aria-label": QUALITY_GUARD_TOGGLE_LABEL,
    "aria-pressed": isGuardianOpen,
    "aria-controls": QUALITY_GUARD_PANEL_ID,
  } as const;
  const guardianToggleClassName = cn(
    QUALITY_GUARD_TOGGLE_CLASS,
    isGuardianOpen && "bg-surface-blue"
  );

  if (state === undefined) {
    return <Skeleton className="h-6 w-6" />;
  }

  const projection = entityHeaderProjection(state);
  const scanFailed = state.scanState === "failed";
  const combinedIssueState = projection
    ? combineQualityGuardIssueState(projection)
    : { count: 0, severity: "none" as const };

  const openWork = entityOpenWork(state);

  const toggleEntityQualityGuardPanel = () => {
    if (!isGuardianOpen) {
      setEntityContext(entityTypeKey, entityId, entityName, { tabKey: null, fieldId: null });
    }
    togglePanel("guardian");
  };

  /**
   * Navigating to one group is not toggling the panel. A marker that closed the sidebar when it
   * was already open did the opposite of what it says, and it reached the group only from the one
   * state where the panel happened to be shut — so this always names the group and only ever
   * opens. Toggling stays the shield's.
   */
  const openEntityQualityGuardGroup = (groupKey: string) => {
    setEntityContext(entityTypeKey, entityId, entityName, {
      tabKey: null,
      fieldId: null,
      qualityGuardGroupKey: groupKey,
    });
    if (!isGuardianOpen) {
      togglePanel("guardian");
    }
  };

  // A sibling of the shield, never inside it: the shield is a toggle, this is a second
  // reading with its own way into the panel, and one button cannot be nested in another.
  const openWorkTag = (
    <QualityGuardOpenWorkTag
      count={openWork.count}
      summaries={openWork.summaries}
      onClick={() => openEntityQualityGuardGroup(QUALITY_GUARD_OPEN_WORK_GROUP_KEY)}
    />
  );

  if (!projection) {
    return (
      <span className="inline-flex items-center gap-3">
        <button
          {...guardianToggleProps}
          className={cn(guardianToggleClassName, "text-muted-foreground")}
          onClick={() => toggleEntityQualityGuardPanel()}
          title="Quality Guard not checked yet"
          data-qg-visual-state="not-checked"
        >
          <ShieldQuestionMark className="size-4" strokeWidth={1.8} aria-hidden />
        </button>
        {openWorkTag}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-3">
      <button
        {...guardianToggleProps}
        className={cn(guardianToggleClassName, "text-left")}
        onClick={() => toggleEntityQualityGuardPanel()}
      >
        {scanFailed ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive">
            <ShieldAlert className="size-4" aria-hidden />
            Scan failed
          </span>
        ) : (
          <QualityGuardIssueBadge
            label="Quality Guard"
            count={combinedIssueState.count}
            severity={combinedIssueState.severity}
            checking={projection.checking}
            evaluationState={projection.evaluationState}
            evaluationCounts={projection.evaluationCountsJson}
          />
        )}
      </button>
      {openWorkTag}
    </span>
  );
}

function toFixFindingInput(finding: QualityGuardFinding): QualityGuardFixFindingInput {
  const remediation = getFindingRemediation(finding);
  return {
    findingId: String(finding._id),
    canonicalKey: finding.canonicalKey,
    ruleId: finding.ruleId ? String(finding.ruleId) : null,
    tabKey: finding.tabKey,
    fieldId: finding.fieldId,
    title: finding.title,
    message: finding.message,
    missingAspects: finding.missingAspectsJson ?? [],
    remediation: remediation
      ? {
          requiredFix: remediation.requiredFix,
          clarificationQuestion: remediation.clarificationQuestion,
          remediationRoute: remediation.remediationRoute,
        }
      : null,
  };
}

/**
 * LAT-934: aspect-first Guardian panel — three always-visible groups (Completeness / Quality /
 * Accepted; see `quality-guard-types.ts` for why the group list is structured to take a fourth
 * later without rework). There is no selection UI: per the founder's 2026-08-24 preview review
 * (reversing the 2026-08-23 aspect-selection decision after live-testing showed Claudia always
 * fixes the whole finding regardless of a "focus on these aspects" narrowing), every card's
 * `onFixWithClaudia` / `onAcceptRisk` acts on the whole finding. This component just resolves
 * display labels/titles per finding and wires those callbacks to the single-finding Claudia
 * intent builder and the whole-finding accept-risk mutation.
 */
export function QualityGuardEntityPanel({
  entityName,
  projectId,
  entityTypeKey,
  entityId,
  sectionTabKey,
  sectionFieldId,
}: QualityGuardEntityArgs & {
  entityName?: string | null;
  sectionTabKey?: string | null;
  sectionFieldId?: string | null;
}) {
  const state = useQuery(api.qualityGuard.getEntityState, {
    projectId,
    entityTypeKey,
    entityId,
  });
  const operatorState = useQuery(api.qualityGuard.canOperateScope, { projectId });
  const riskAcceptanceState = useQuery(api.qualityGuard.canAcceptRiskScope, { projectId });
  const projectSearchParams = useProjectSearchParams();
  const panelFocus = useMemo(
    () => parseQualityGuardPanelFocus(projectSearchParams),
    [projectSearchParams]
  );
  const userMap = useOrgUserMap();
  const forceRefreshScope = useObservedMutation(api.qualityGuard.forceRefreshScope, {
    feature: "quality-guard",
    operation: "forceRefreshScope",
    includeRequestId: true,
  });
  const acceptRisk = useObservedMutation(api.qualityGuard.acceptRisk, {
    feature: "quality-guard",
    operation: "acceptRisk",
    includeRequestId: true,
  });
  const revokeAcceptedRisk = useObservedMutation(api.qualityGuard.revokeAcceptedRisk, {
    feature: "quality-guard",
    operation: "revokeAcceptedRisk",
    includeRequestId: true,
  });
  // QG2's own accept/withdraw pair. Which one a click reaches is decided by the row it was
  // clicked on, never by the flag: a row carries the engine that wrote it, and a panel
  // still showing a superseded engine's rows must not write to the new one.
  const acceptQualityGuard2Risk = useObservedMutation(
    api.qualityGuard2Acceptance.recordRiskAcceptance,
    {
      feature: "quality-guard",
      operation: "qg2RecordRiskAcceptance",
      includeRequestId: true,
    }
  );
  const withdrawQualityGuard2Risk = useObservedMutation(
    api.qualityGuard2Acceptance.withdrawRiskAcceptance,
    {
      feature: "quality-guard",
      operation: "qg2WithdrawRiskAcceptance",
      includeRequestId: true,
    }
  );
  // LAT-934: the accept-risk target is always the whole finding — no aspect-level selection.
  const [riskTarget, setRiskTarget] = useState<QualityGuardRiskTarget | null>(null);
  const [riskReason, setRiskReason] = useState("");
  const [pendingRiskAction, setPendingRiskAction] = useState(false);
  const [pendingManualRefresh, setPendingManualRefresh] = useState(false);
  const trackedOpenKeyRef = useRef<string | null>(null);
  const appliedPanelFocusKeyRef = useRef<string | null>(null);
  const panelRootRef = useRef<HTMLDivElement>(null);
  const [expandedFindingIds, setExpandedFindingIds] = useState<Set<string>>(new Set());
  const toggleFinding = (findingId: string) =>
    setExpandedFindingIds((current) => {
      const next = new Set(current);
      if (next.has(findingId)) {
        next.delete(findingId);
      } else {
        next.add(findingId);
      }
      return next;
    });

  const focusableFindings = useMemo(
    () =>
      state === undefined
        ? []
        : sortFindings(
            (state.findings as QualityGuardFinding[]).filter((finding) =>
              findingMatchesSection(finding, {
                tabKey: sectionTabKey,
                fieldId: sectionFieldId,
              })
            )
          ),
    [sectionFieldId, sectionTabKey, state]
  );
  const focusableAcceptedRisks = useMemo(
    () =>
      state === undefined
        ? []
        : ((state.acceptedRisks ?? []) as QualityGuardAcceptedRisk[]).filter((risk) =>
            riskMatchesSection(risk, {
              tabKey: sectionTabKey,
              fieldId: sectionFieldId,
            })
          ),
    [sectionFieldId, sectionTabKey, state]
  );
  const panelFocusChecking = Boolean(
    state === undefined || pendingManualRefresh || state.checking || state.projection?.checking
  );

  useEffect(() => {
    if (!panelFocus || panelFocusChecking) {
      return;
    }
    const focusKey = `${entityTypeKey}:${entityId}:${panelFocus.focus}:${panelFocus.findingId ?? "group"}`;
    if (appliedPanelFocusKeyRef.current === focusKey) {
      return;
    }

    const scrollToTarget = (attribute: string, value?: string) => {
      window.setTimeout(() => {
        const candidates = panelRootRef.current?.querySelectorAll<HTMLElement>(`[${attribute}]`);
        const target = value
          ? Array.from(candidates ?? []).find(
              (candidate) => candidate.getAttribute(attribute) === value
            )
          : candidates?.item(0);
        target?.scrollIntoView?.({ block: "nearest" });
      }, 0);
    };

    if (panelFocus.focus === PROJECT_QUALITY_GUARD_PANEL_FOCUS.accepted) {
      if (focusableAcceptedRisks.length === 0) {
        return;
      }
      appliedPanelFocusKeyRef.current = focusKey;
      scrollToTarget("data-qg-accepted-risk-row");
      return;
    }

    // A caller that names one issue names the ROW — a QG2 finding with three open accept
    // keys is three cards, and the finding's id picks out all three and opens none of them,
    // because a card is expanded and addressed by its own row key.
    const targetFinding = panelFocus.findingId
      ? focusableFindings.find(
          (finding) => qualityGuardFindingRowKey(finding) === panelFocus.findingId
        )
      : focusableFindings.find((finding) => finding.criticality === panelFocus.focus);
    if (!targetFinding) {
      return;
    }
    const targetRowKey = qualityGuardFindingRowKey(targetFinding);
    appliedPanelFocusKeyRef.current = focusKey;
    setExpandedFindingIds(new Set([targetRowKey]));
    scrollToTarget("data-qg-finding-row-key", targetRowKey);
  }, [
    entityId,
    entityTypeKey,
    focusableAcceptedRisks,
    focusableFindings,
    panelFocus,
    panelFocusChecking,
  ]);

  const { entityQualityGuardGroupKey, clearEntityQualityGuardGroupKey } = useRightSidebarEntity();
  // A caller that asked for one group — the open-work marker is the only one today —
  // scrolls it into view once and then releases the request, so the next open of the same
  // entity lands where the reader left off instead of being dragged back here. Waits out
  // an in-flight check for the same reason the focus effect above does: the group it is
  // looking for may not be on screen until the reading settles.
  useEffect(() => {
    if (!entityQualityGuardGroupKey || panelFocusChecking) {
      return;
    }
    const target = panelRootRef.current?.querySelector<HTMLElement>(
      `[data-qg-group-key="${entityQualityGuardGroupKey}"]`
    );
    if (!target) {
      return;
    }
    target.scrollIntoView?.({ block: "nearest" });
    clearEntityQualityGuardGroupKey();
  }, [clearEntityQualityGuardGroupKey, entityQualityGuardGroupKey, panelFocusChecking]);

  // LAT-620: "Fix with Claudia" needs real section labels for the entity-section handoff.
  // The card only carries raw tabKey/fieldId, so resolve labels from the same compiled
  // framework structure the entity page uses, falling back to humanized keys.
  const { expandToPanel } = useRightSidebarUi();
  const { pendingAskClaudiaIntent, setAskClaudiaIntent } = useRightSidebarClaudia();
  const frameworkStructure = useProjectFrameworkStructure(projectId);
  const resolveSectionLabels = useMemo(() => {
    let tabs: ReturnType<typeof resolveEntityAuthoringTabDescriptors> | null = null;
    try {
      tabs = resolveEntityAuthoringTabDescriptors({
        entityTypeKey,
        compiledStructure: frameworkStructure ?? null,
        compiledTemplateJson: frameworkStructure?.compiledTemplateJson ?? null,
      });
    } catch {
      // Structure not loaded yet (or unknown entity type) — fall back to raw keys.
      tabs = null;
    }
    return (
      tabKey: string | null | undefined,
      fieldId: string | null | undefined
    ): { tabLabel?: string; fieldLabel?: string } => {
      if (!tabs || !tabKey) {
        return {};
      }
      const tab = tabs.find((candidate) => candidate.tabKey === tabKey);
      return {
        tabLabel: tab?.label,
        fieldLabel: fieldId
          ? tab?.sections.find((section) => section.fieldId === fieldId)?.label
          : undefined,
      };
    };
  }, [entityTypeKey, frameworkStructure]);

  const canFixFinding = (finding: QualityGuardFinding): boolean =>
    Boolean(finding.tabKey?.trim() && finding.fieldId?.trim());

  // LAT-935 double-click guard. The ask-intent slot holds exactly ONE intent, so a second click
  // does not queue behind the first — it REPLACES it with a fresh `requestKey`, and the panel
  // then creates a second thread and opens a second run for the same finding. That is the
  // click-storm Galina hit (seven clicks, seven runs, each cancelling the last).
  //
  // Two layers, because they fail at different moments: the ref closes the same-tick window
  // before React has re-rendered anything, and `pendingAskClaudiaIntent` keeps the button
  // disabled across the whole async create-thread → submit round trip. The guard reopens when
  // the intent slot clears — which the composer does on a successful send, and "New chat" /
  // history-switch do on abandonment. A *failed* submit deliberately leaves it closed: the
  // prompt is still sitting in the composer, and the recovery is Send, not another click.
  const fixClickGuardRef = useRef(false);
  const [isFixIntentPending, setIsFixIntentPending] = useState(false);
  useEffect(() => {
    if (!pendingAskClaudiaIntent) {
      fixClickGuardRef.current = false;
      setIsFixIntentPending(false);
    }
  }, [pendingAskClaudiaIntent]);

  // LAT-934 founder decision (2026-08-24, reversing points 3-5): no aspect selection — Claudia
  // always attempts to fix the whole finding from her own reading of the objects/evidence, and
  // marks anything she can't resolve as open in her reply text. One finding ref through the
  // existing single-finding channel, whole-finding payload, no "focusing on" narrowing.
  const handleFixWithClaudia = (finding: QualityGuardFinding) => {
    if (fixClickGuardRef.current) {
      return;
    }
    const intent = buildClaudiaFixIntent({
      finding: toFixFindingInput(finding),
      entityCtx: { entityTypeKey, entityId, entityName },
      labels: resolveSectionLabels(finding.tabKey, finding.fieldId),
    });
    if (!intent) {
      return;
    }
    fixClickGuardRef.current = true;
    setIsFixIntentPending(true);
    setAskClaudiaIntent(intent);
    expandToPanel("claudia");
  };

  useEffect(() => {
    const openKey = `${projectId}:${entityTypeKey}:${entityId}`;
    if (trackedOpenKeyRef.current === openKey) return;
    trackedOpenKeyRef.current = openKey;

    trackQualityGuardEvent("quality_guard_opened", {
      scope_kind: "entity",
      surface: "right_panel",
      project_id: projectId,
      entity_type_key: entityTypeKey,
      entity_id: entityId,
    });
  }, [entityId, entityTypeKey, projectId]);

  // LAT-934 founder decision (2026-08-24, reversing points 3-5): no aspect selection — Accept
  // risk always applies to the whole finding, one `acceptRisk` call with no `aspectId`.
  async function handleAcceptRisk() {
    if (!riskTarget || !riskReason.trim() || acceptRiskRefusalReason(riskTarget.finding) !== null) {
      return;
    }
    const finding = riskTarget.finding;
    setPendingRiskAction(true);
    try {
      // Both engines refuse an unsafe finding with the same `qg_risk_acceptance_unsafe_forbidden`
      // code, and QG2's top-tier refusal is its sibling — so one catch and one message
      // covers every refusal, and the panel never learns which engine said no.
      if (finding.engine === "quality_guard_2") {
        await acceptQualityGuard2Risk({
          projectId,
          // The panel row carries the finding's id as the string the projection sends; the
          // write path names the document it addresses.
          findingId: finding._id as Id<"qualityGuard2Findings">,
          acceptKey: finding.acceptKey,
          note: riskReason.trim(),
        });
      } else {
        await acceptRisk({
          projectId,
          findingId: finding._id,
          reason: riskReason.trim(),
        });
      }
      toast.success("Quality Guard risk accepted");
      setRiskTarget(null);
      setRiskReason("");
    } catch {
      toast.error("Failed to accept Quality Guard risk");
    } finally {
      setPendingRiskAction(false);
    }
  }

  async function handleRevokeRisk(risk: QualityGuardAcceptedRisk) {
    setPendingRiskAction(true);
    try {
      if (risk.engine === "quality_guard_2") {
        // QG2 records no reason for a withdrawal: the acceptance is the decision that
        // needed one, and withdrawing it restores the issue rather than adding a claim.
        await withdrawQualityGuard2Risk({ projectId, acceptedRiskId: risk.acceptedRiskId });
      } else {
        await revokeAcceptedRisk({
          projectId,
          acceptedRiskId: risk.acceptedRiskId,
          reason: "Revoked from Quality Guard entity panel",
        });
      }
      toast.success("Accepted risk revoked");
    } catch {
      toast.error("Failed to revoke accepted risk");
    } finally {
      setPendingRiskAction(false);
    }
  }

  async function handleRunEntityScan() {
    setPendingManualRefresh(true);
    trackQualityGuardEvent("quality_guard_scan_started", {
      scope_kind: "entity",
      project_id: projectId,
      entity_type_key: entityTypeKey,
      entity_id: entityId,
    });
    try {
      await forceRefreshScope({
        projectId,
        scopeKind: "entity",
        entityTypeKey,
        entityId,
        requestId: `manual-entity-scan-${entityTypeKey}-${entityId}-${Date.now()}`,
        reason: "Manual entity Quality Guard scan from UI",
      });
      notifyQualityGuardScanStarted(QUALITY_GUARD_ENTITY_SCAN_STARTED_MESSAGE);
    } catch (error) {
      toast.error(getConvexErrorMessage(error) ?? "Failed to start Quality Guard entity scan");
    } finally {
      setPendingManualRefresh(false);
    }
  }

  if (state === undefined) {
    return (
      <ScrollArea className="min-h-0 flex-1 px-4">
        <div className="space-y-3 py-4">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-20 w-full rounded-md" />
          <Skeleton className="h-24 w-full rounded-md" />
        </div>
      </ScrollArea>
    );
  }

  const projection = entityHeaderProjection(state);
  const isSectionScoped = Boolean(sectionFieldId);
  const sectionProjections = (state.sectionProjections ?? []) as QualityGuardSectionProjection[];
  const selectedSectionProjection = sectionProjections.find(
    (entry) =>
      sectionFieldId &&
      entry.fieldId === sectionFieldId &&
      (!sectionTabKey || !entry.tabKey || entry.tabKey === sectionTabKey)
  );
  // The roll-up row and the issues it counts are written separately, so an element can hold
  // current issues while its row is still pending. Reading only the row then prints "nothing
  // produced yet" directly above a list of four issues. The issues in hand answer the header's
  // own question, and `rollupPending` below says the counts came from them.
  const derivedProjection = deriveHeaderProjectionFromFindings(focusableFindings, {
    checking: Boolean(state.checking),
    acceptedRisks: focusableAcceptedRisks,
  });
  // A section-scoped panel falls back to the section's own issues, never to the element's
  // roll-up: that row counts every section, and printing it above a list filtered to one of
  // them reports the element's issues as this section's.
  const rollupProjection = isSectionScoped
    ? (selectedSectionProjection?.projection ?? null)
    : projection;
  const rollupPending = rollupProjection === null && derivedProjection !== null;
  const displayProjection = rollupProjection ?? derivedProjection;
  // LAT-858 P5: the entity-wide coverage footnote below (`state.coverage`, from the latest
  // check-run) has no per-section breakdown, so it was previously hidden whenever a section was
  // selected. `evaluationCountsJson` is per-projection, so a selected section can now show its
  // own coverage instead of nothing. Only surface it when there is an actual gap to report —
  // fully-judged sections stay silent, same as the entity-wide footnote does today.
  const selectedSectionEvaluationCounts =
    selectedSectionProjection?.projection.evaluationCountsJson;
  const selectedSectionEvaluationSummary =
    selectedSectionEvaluationCounts &&
    formatQualityGuardIssueCount(selectedSectionEvaluationCounts.unverified) +
      formatQualityGuardIssueCount(selectedSectionEvaluationCounts.errored) +
      formatQualityGuardIssueCount(selectedSectionEvaluationCounts.notApplicable) >
      0
      ? formatQualityGuardEvaluationCounts(selectedSectionEvaluationCounts)
      : null;
  const completenessIssueState = displayProjection
    ? combineQualityGuardIssueState({
        completenessIssueCount: displayProjection.completenessIssueCount,
        completenessHighestCriticality: displayProjection.completenessHighestCriticality,
        completenessSeverity: displayProjection.completenessSeverity,
      })
    : null;
  const qualityIssueState = displayProjection
    ? combineQualityGuardIssueState({
        qualityIssueCount: displayProjection.qualityIssueCount,
        qualityHighestCriticality: displayProjection.qualityHighestCriticality,
        qualitySeverity: displayProjection.qualitySeverity,
      })
    : null;
  const isChecking = Boolean(
    pendingManualRefresh || state.checking || projection?.checking || displayProjection?.checking
  );
  const scanFailed = state.scanState === "failed";
  const findings = focusableFindings;
  const acceptedRisks = focusableAcceptedRisks;
  // The larger of the two against a ROLL-UP, because `getEntityState` truncates its
  // project-wide accepted-risk scan before filtering it to this scope and the array can
  // therefore be shorter than the row counted. Against a derived reading it is the derived
  // count alone: that one was computed from this very array, and counts the findings the
  // acceptances cover rather than the acceptances themselves — the same number the roll-up
  // will report — so taking the longer array back would put three rows of one finding under
  // a badge that drops to one the moment the row lands.
  const displayedAcceptedRiskCount = rollupPending
    ? formatQualityGuardIssueCount(displayProjection?.acceptedRiskCount)
    : Math.max(
        formatQualityGuardIssueCount(displayProjection?.acceptedRiskCount),
        acceptedRisks.length
      );
  const title = entityName?.trim() || "Current entity";
  const scopeLabel = sectionFieldId ? humanizeKey(sectionFieldId) : null;
  const canRunEntityScan = operatorState?.canOperate === true;

  const canAcceptProjectRisk = riskAcceptanceState?.canAcceptProjectRisk === true;
  const canManageAcceptedRisks = canAcceptProjectRisk;
  const canAcceptFindingRisk = (finding: QualityGuardFinding): boolean =>
    canAcceptProjectRisk ||
    (riskAcceptanceState?.userId !== undefined &&
      finding.ownerHintUserId === riskAcceptanceState.userId);
  // "Did Quality Guard actually finish checking this?" — the incumbent answers with a rule
  // count, QG2 has no rules to count. Its readiness row is written when a check settles, so
  // the row's existence is that same fact, and reading it any other way would leave a clean
  // QG2 scope permanently short of the green state.
  const hasCompletedChecks =
    displayProjection?.engine === "quality_guard_2"
      ? true
      : displayProjection && typeof displayProjection.completedRuleCount === "number"
        ? displayProjection.completedRuleCount > 0
        : false;
  // LAT-858 P5: a clean scan (no open findings) only earns the green "Everything checks out"
  // state when Quality Guard actually finished evaluating it. "partial"/"unchecked" mean some
  // rules never got a verdict — silence there would read as a false-positive pass, so it routes
  // to the warning-toned callout below instead. Absent evaluationState (legacy rows) keeps the
  // green state, matching prior behavior exactly.
  const isPartiallyEvaluated =
    displayProjection?.evaluationState === "partial" ||
    displayProjection?.evaluationState === "unchecked";
  // LAT-934 review (Codex P1): accepted risk is NOT a pass (platform doctrine) — the green
  // "confirmed"/"verified" success copy must never render while an accepted risk still applies.
  // `hasNoOpenOrAcceptedItems` gates the true "everything checks out" state; a scope with zero
  // open findings but ≥1 accepted risk gets its own neutral branch below instead.
  //
  // The count, not the returned detail array: `getEntityState` truncates its project-wide
  // accepted-risk scan before filtering it to this scope, so an older accepted risk can leave
  // the array empty while the projection still counts it — and the Accepted badge above renders
  // that same count. Reading the array alone put the green state directly beneath a nonzero
  // Accepted badge. It is the same number both places or it is a contradiction on one screen.
  const hasNoOpenOrAcceptedItems = displayedAcceptedRiskCount === 0;
  // A section that does not apply to this content is not a gap (that is
  // `isPartiallyEvaluated`'s claim) and not a defect — it is nothing to check, and the
  // green "Everything checks out" must never speak for it. `displayProjection` is a leaf
  // row either way — the selected section's own, or (for QG2) the entity's own row with
  // `evaluationState` already resolved against its own coherence view, never a bare fold
  // (`panelRead.ts`/`panelProjection.ts`: a fold alone cannot tell "every section was
  // empty and the entity view too" from "the entity view was genuinely checked"). The
  // per-section count is still folded here, but only for the "Everything checks out"
  // description below, which names how many sections did not apply.
  const notApplicableSectionCount =
    displayProjection?.engine === "quality_guard_2" && !isSectionScoped
      ? sectionProjections.filter((entry) => entry.projection.evaluationState === "not_applicable")
          .length
      : 0;
  const isFullyNotApplicable = displayProjection?.evaluationState === "not_applicable";
  const canShowNoCurrentIssues = Boolean(
    displayProjection &&
      !isChecking &&
      !scanFailed &&
      hasCompletedChecks &&
      !isPartiallyEvaluated &&
      !isFullyNotApplicable &&
      hasNoOpenOrAcceptedItems
  );
  const showFullyNotApplicableState = Boolean(
    displayProjection &&
      !isChecking &&
      !scanFailed &&
      hasCompletedChecks &&
      isFullyNotApplicable &&
      hasNoOpenOrAcceptedItems
  );
  const fullyNotApplicableCallout = showFullyNotApplicableState
    ? formatNotApplicableEvaluationState(isSectionScoped)
    : null;
  const showAcceptedRiskOnlyState = Boolean(
    displayProjection &&
      !isChecking &&
      !scanFailed &&
      hasCompletedChecks &&
      !isPartiallyEvaluated &&
      !hasNoOpenOrAcceptedItems
  );
  // LAT-858 (Codex review): the callout must NOT require completed checks —
  // for a real "unchecked" projection `judged` is zero by definition, so
  // `hasCompletedChecks` is necessarily false and gating on it made
  // "Couldn't check" unreachable for all-unverified/all-error scans.
  const showPartialEvaluationState = Boolean(
    displayProjection && !isChecking && !scanFailed && isPartiallyEvaluated
  );
  const partialEvaluationCallout = showPartialEvaluationState
    ? formatPartialEvaluationCallout(
        displayProjection?.evaluationState,
        displayProjection?.evaluationCountsJson
      )
    : null;

  function isFindingSectionChecking(finding: QualityGuardFinding): boolean {
    if (!finding.fieldId) {
      return false;
    }
    const entry = sectionProjections.find(
      (candidate) =>
        candidate.fieldId === finding.fieldId &&
        (!candidate.tabKey || !finding.tabKey || candidate.tabKey === finding.tabKey)
    );
    return Boolean(entry?.projection.checking);
  }

  const openFindingGroups = groupOpenFindings(findings);

  return (
    <div ref={panelRootRef} className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-3 border-b border-border px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="truncate text-sm font-medium">{title}</p>
            <p className="text-xs text-muted-foreground">
              {isSectionScoped
                ? `Quality Guard issues filtered to ${scopeLabel}.`
                : "Quality Guard results for the latest saved entity version."}
            </p>
          </div>
          {canRunEntityScan ? (
            <Button
              type="button"
              size="sm"
              className="h-7 shrink-0 gap-1.5"
              disabled={isChecking}
              onClick={() => void handleRunEntityScan()}
            >
              {isChecking ? (
                <Spinner data-icon="inline-start" className="size-3.5" />
              ) : (
                <ScanLine data-icon="inline-start" className="size-3.5" />
              )}
              <span>{isChecking ? "Checking..." : "Run scan"}</span>
            </Button>
          ) : null}
        </div>

        {displayProjection ? (
          <div data-testid="quality-guard-axis-summary" className="space-y-2">
            {scanFailed ? (
              <Alert variant="destructive" data-testid="quality-guard-scan-failed-state">
                <ShieldAlert />
                <AlertTitle>Latest scan failed</AlertTitle>
                <AlertDescription>
                  {state.scanError?.message ?? "Run the scan again to retry this entity."}
                </AlertDescription>
              </Alert>
            ) : null}
            {!isChecking && displayProjection.readinessLabel === "unsafe" ? (
              <Alert variant="destructive" data-testid="quality-guard-unsafe-state">
                <ShieldAlert />
                <AlertTitle>Unsafe - safety stop</AlertTitle>
                <AlertDescription>
                  Resolve the unsafe issue before relying on this scope. Risk acceptance is not
                  available.
                </AlertDescription>
              </Alert>
            ) : null}
            <ProjectionIssueRow
              label="Completeness"
              issueCount={completenessIssueState?.count ?? 0}
              severity={completenessIssueState?.severity}
              checking={isChecking}
            />
            <ProjectionIssueRow
              label="Quality"
              issueCount={qualityIssueState?.count ?? 0}
              severity={qualityIssueState?.severity}
              checking={isChecking}
              applicable={displayProjection.qualityApplicable}
            />
            <ProjectionAcceptedRiskRow acceptedRiskCount={displayedAcceptedRiskCount} />
            {selectedSectionProjection ? (
              selectedSectionEvaluationSummary ? (
                <p
                  data-testid="quality-guard-section-evaluation-summary"
                  className="text-[11px] leading-snug text-muted-foreground"
                >
                  This section: {selectedSectionEvaluationSummary}
                </p>
              ) : null
            ) : rollupPending ? (
              <p
                data-testid="quality-guard-rollup-pending"
                className="text-[11px] leading-snug text-muted-foreground"
              >
                Counted from the issues below — the roll-up for this element has not been written
                yet.
              </p>
            ) : state.coverage?.coverageState === "partial" ? (
              <p className="text-[11px] leading-snug text-muted-foreground">
                Quality reflects {state.coverage.evaluatedCoreRuleCount} of{" "}
                {state.coverage.coreRuleCount} core checks; {state.coverage.skippedCoreRuleCount}{" "}
                skipped this scan.
              </p>
            ) : null}
          </div>
        ) : (
          <div className="border border-dashed p-3 text-sm text-muted-foreground">
            Quality Guard has not produced results for this entity yet.
          </div>
        )}
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="pb-4">
          {findings.length > 0 ? (
            <section>
              {openFindingGroups.map((group) => (
                <div key={group.key}>
                  <div
                    data-testid="quality-guard-finding-group-header"
                    data-qg-group-key={group.key}
                    className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-4 py-1.5"
                  >
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </h3>
                    <Badge variant="outline" className="text-[11px]">
                      {group.items.length}
                    </Badge>
                  </div>
                  {group.items.map((finding) => {
                    // The row's identity, not the finding's: a QG2 finding with three open
                    // accept keys renders three cards, and expanding one must not expand
                    // its siblings.
                    const findingId = qualityGuardFindingRowKey(finding);
                    // LAT-934 points 2 & 7: resolved once here (real compiled labels when
                    // available, humanized keys as the only fallback — never a raw key) and fed
                    // into both the card title (section-state sentence) and the breadcrumb.
                    const labels = resolveSectionLabels(finding.tabKey, finding.fieldId);
                    return (
                      <QualityGuardFindingCard
                        key={findingId}
                        finding={finding}
                        title={resolveFindingCardTitle(finding, labels)}
                        location={resolveFindingCardLocation(finding, labels)}
                        isOpen={expandedFindingIds.has(findingId)}
                        onToggleOpen={() => toggleFinding(findingId)}
                        isChecking={isFindingSectionChecking(finding)}
                        canFix={canFixFinding(finding)}
                        fixDisabled={isFixIntentPending || Boolean(pendingAskClaudiaIntent)}
                        onFixWithClaudia={() => handleFixWithClaudia(finding)}
                        canAcceptRisk={canAcceptFindingRisk(finding)}
                        acceptRiskRefusal={acceptRiskRefusalReason(finding)}
                        onAcceptRisk={() => {
                          setRiskTarget({ finding });
                          setRiskReason("");
                        }}
                      />
                    );
                  })}
                </div>
              ))}
            </section>
          ) : fullyNotApplicableCallout ? (
            // Nothing here was judged clean — it was never sent for a ruling at all — so
            // this is deliberately neither the green success state nor
            // the warning-toned gap callout below. Muted, same family as the per-section
            // badge's own "Not applicable" reading (`issue-badge.tsx`).
            <StatusPanel
              data-testid="quality-guard-not-applicable-state"
              size="compact"
              icon={ShieldOff}
              iconClassName="text-muted-foreground"
              title={fullyNotApplicableCallout.title}
              description={fullyNotApplicableCallout.description}
            />
          ) : canShowNoCurrentIssues ? (
            <StatusPanel
              data-testid="quality-guard-empty-state"
              size="compact"
              tone="success"
              icon={ShieldCheck}
              title="Everything checks out"
              description={formatCleanScanDescription(isSectionScoped, notApplicableSectionCount)}
            />
          ) : showAcceptedRiskOnlyState ? (
            // LAT-934 review (Codex P1): deliberately NOT the green success state — accepted risk
            // is not a pass. Neutral copy, no "confirmed"/"verified" claim, and the accepted-risk
            // count instead of an implied all-clear.
            <StatusPanel
              data-testid="quality-guard-accepted-risk-only-state"
              size="compact"
              icon={ShieldCheck}
              iconClassName={qualityGuardAcceptedRiskVisuals.textClassName}
              title="No open findings"
              description={
                <>
                  {displayedAcceptedRiskCount} accepted risk
                  {displayedAcceptedRiskCount === 1 ? "" : "s"} still{" "}
                  {displayedAcceptedRiskCount === 1 ? "applies" : "apply"} to this{" "}
                  {isSectionScoped ? "section" : "entity"} — accepted risk is not a pass.
                </>
              }
            />
          ) : partialEvaluationCallout ? (
            <StatusPanel
              data-testid="quality-guard-partial-evaluation-state"
              size="compact"
              tone="warning"
              icon={ShieldAlert}
              title={partialEvaluationCallout.title}
              description={partialEvaluationCallout.description}
            />
          ) : displayProjection ? (
            <StatusPanel
              size="compact"
              icon={CircleDashed}
              title={isChecking ? "Checking latest version" : "No completed checks yet"}
              description="Quality Guard has not completed a finding-producing check for this entity."
            />
          ) : (
            <StatusPanel
              size="compact"
              icon={ShieldAlert}
              title="No issue data yet"
              description="Save the entity to trigger a Quality Guard run."
            />
          )}

          {acceptedRisks.length > 0 ? (
            <section data-testid="quality-guard-accepted-risk-section">
              <div
                data-testid="quality-guard-finding-group-header"
                data-qg-group-key="accepted"
                className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-4 py-1.5"
              >
                <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {QUALITY_GUARD_ACCEPTED_GROUP_LABEL}
                </h3>
                <QualityGuardAcceptedRiskBadge
                  label="Accepted risks"
                  count={acceptedRisks.length}
                />
              </div>
              <div>
                {acceptedRisks.map((risk) => (
                  <div
                    key={risk.acceptedRiskId}
                    data-qg-accepted-risk-row="true"
                    className="border-b border-border px-4 py-2.5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-2">
                        <ShieldCheck
                          data-qg-accepted-risk-icon="true"
                          data-qg-visual-state="accepted"
                          className={cn(
                            "mt-0.5 size-4 shrink-0",
                            qualityGuardAcceptedRiskVisuals.textClassName
                          )}
                          strokeWidth={1.8}
                          aria-hidden
                        />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{risk.findingTitle}</p>
                          <AcceptedRiskContext risk={risk} userMap={userMap} />
                        </div>
                      </div>
                      {canManageAcceptedRisks ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 shrink-0 text-xs"
                          disabled={pendingRiskAction}
                          onClick={() => void handleRevokeRisk(risk)}
                        >
                          Revoke
                        </Button>
                      ) : null}
                    </div>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {risk.fieldId ? humanizeKey(risk.fieldId) : risk.canonicalKey}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </ScrollArea>
      <AdminFormDialog
        open={riskTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRiskTarget(null);
            setRiskReason("");
          }
        }}
        title="Accept Quality Guard risk"
        description="Record why this issue is acceptable for now. Accepted risk does not count as a pass."
        submitLabel="Accept risk"
        submitDisabled={
          !riskReason.trim() ||
          pendingRiskAction ||
          (riskTarget !== null && acceptRiskRefusalReason(riskTarget.finding) !== null)
        }
        onSubmit={() => void handleAcceptRisk()}
        size="sm"
      >
        <div className="space-y-2">
          <p className="font-medium text-sm">{riskTarget?.finding.title}</p>
          <Textarea
            value={riskReason}
            onChange={(event) => setRiskReason(event.target.value)}
            placeholder="Reason this risk is acceptable"
            className="min-h-24 text-sm"
          />
        </div>
      </AdminFormDialog>
    </div>
  );
}
